import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AuctionDashboardTab - Auction analytics overview and recent activity
 * Shows stat cards, recent auctions, and quick navigation
 */
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaGavel, FaCalendarAlt, FaCheckCircle, FaDollarSign, FaSync, FaClock, FaUsers, FaArrowRight, } from 'react-icons/fa';
import { auctionService } from '../../../services/auctionService';
// ── Styled Components ────────────────────────────────────────
const Container = styled.div `
  padding: 0;
`;
const HeaderActions = styled.div `
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;
const RefreshButton = styled.button `
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  border: 1px solid var(--accent-gold);
  background: var(--accent-gold);
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: var(--accent-gold-dark);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    animation: ${props => props.$loading ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const StatCard = styled.div `
  background: linear-gradient(135deg, ${props => props.$color}15 0%, ${props => props.$color}05 100%);
  border: 1px solid ${props => props.$color}30;
  border-left: 4px solid ${props => props.$color};
  border-radius: 12px;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px ${props => props.$color}20;
    border-color: ${props => props.$color};
  }
`;
const StatIcon = styled.div `
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 2rem;
  color: ${props => props.$color};
  opacity: 0.25;
`;
const StatLabel = styled.div `
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-tertiary);
  font-weight: 600;
  margin-bottom: 0.5rem;
`;
const StatValue = styled.div `
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
  line-height: 1;
`;
const StatSubtext = styled.div `
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
`;
const Section = styled.div `
  margin-bottom: 2rem;
`;
const SectionHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${props => props.$color || 'var(--border-color)'};
`;
const SectionTitle = styled.h3 `
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const SectionBadge = styled.span `
  background: ${props => props.$color || '#6b7280'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
`;
const TableContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow-x: auto;
  max-width: 100%;
`;
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
`;
const Th = styled.th `
  text-align: left;
  padding: 1rem;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
  white-space: nowrap;
`;
const Td = styled.td `
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.875rem;
  color: var(--text-primary);
`;
const Tr = styled.tr `
  &:hover {
    background: var(--bg-tertiary);
  }

  &:last-child td {
    border-bottom: none;
  }
`;
const StatusBadge = styled.span `
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    switch (props.$status) {
        case 'active': return 'rgba(16, 185, 129, 0.15)';
        case 'scheduled': return 'rgba(59, 130, 246, 0.15)';
        case 'grace_period': return 'rgba(245, 158, 11, 0.15)';
        case 'closed': return 'rgba(107, 114, 128, 0.15)';
        case 'cancelled': return 'rgba(239, 68, 68, 0.15)';
        default: return 'rgba(107, 114, 128, 0.15)';
    }
}};
  color: ${props => {
    switch (props.$status) {
        case 'active': return '#10b981';
        case 'scheduled': return '#3b82f6';
        case 'grace_period': return '#f59e0b';
        case 'closed': return '#6b7280';
        case 'cancelled': return '#ef4444';
        default: return '#6b7280';
    }
}};
`;
const TypeBadge = styled.span `
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.$type === 'supervised' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(6, 182, 212, 0.15)'};
  color: ${props => props.$type === 'supervised' ? '#8b5cf6' : '#06b6d4'};
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: var(--accent-gold);
  font-family: 'JetBrains Mono', monospace;
`;
const ViewAllButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85rem;

  &:hover {
    border-color: var(--accent-gold);
    color: var(--accent-gold);
    transform: translateY(-1px);
  }
`;
const QuickActionsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const QuickActionCard = styled.button `
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    border-color: var(--accent-gold);
    background: var(--bg-tertiary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  svg {
    font-size: 1.5rem;
    color: var(--accent-gold);
    flex-shrink: 0;
  }
`;
const QuickActionText = styled.div `
  h4 {
    margin: 0 0 0.25rem 0;
    font-size: 0.9rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }
`;
const AnalyticsRow = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const AnalyticsCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 1.25rem;
  text-align: center;
`;
const AnalyticsLabel = styled.div `
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-tertiary);
  font-weight: 600;
  margin-bottom: 0.5rem;
`;
const AnalyticsValue = styled.div `
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
`;
const LoadingState = styled.div `
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-tertiary);

  svg {
    font-size: 4rem;
    color: var(--accent-gold);
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h3 {
    color: var(--text-primary);
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;
// ── Component ────────────────────────────────────────────────
const AuctionDashboardTab = ({ onNavigateToTab }) => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [recentAuctions, setRecentAuctions] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [scheduledCount, setScheduledCount] = useState(0);
    const [closedThisMonth, setClosedThisMonth] = useState(0);
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch analytics and recent auctions in parallel
            const [analyticsResult, listResult] = await Promise.all([
                auctionService.getAnalytics(),
                auctionService.listAuctions({ pageSize: 10 }),
            ]);
            if (analyticsResult.success) {
                setAnalytics(analyticsResult.data);
            }
            if (listResult.success && listResult.data) {
                const auctions = listResult.data.auctions;
                setRecentAuctions(auctions);
                // Count by status
                setActiveCount(auctions.filter(a => a.status === 'active' || a.status === 'grace_period').length);
                setScheduledCount(auctions.filter(a => a.status === 'scheduled').length);
                // Closed this month
                const now = new Date();
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                setClosedThisMonth(auctions.filter(a => a.status === 'closed' && a.actualEndTime && new Date(a.actualEndTime) >= monthStart).length);
            }
        }
        catch {
            // Silently handle errors - will show empty state
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    const formatCurrency = (value) => {
        if (value >= 1000000)
            return `$${(value / 1000000).toFixed(2)}M`;
        if (value >= 1000)
            return `$${(value / 1000).toFixed(1)}K`;
        return `$${value.toLocaleString()}`;
    };
    const formatTimeRemaining = (endTime) => {
        const end = new Date(endTime);
        const now = new Date();
        const diff = end.getTime() - now.getTime();
        if (diff <= 0)
            return 'Ended';
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 24)
            return `${Math.floor(hours / 24)}d ${hours % 24}h`;
        if (hours > 0)
            return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };
    if (loading) {
        return (_jsx(Container, { children: _jsx(LoadingState, { children: "Loading auction data..." }) }));
    }
    return (_jsxs(Container, { children: [_jsx(HeaderActions, { children: _jsxs(RefreshButton, { onClick: fetchData, "$loading": loading, disabled: loading, children: [_jsx(FaSync, {}), " ", loading ? 'Loading...' : 'Refresh'] }) }), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { "$color": "#10b981", onClick: () => onNavigateToTab('active'), children: [_jsx(StatIcon, { "$color": "#10b981", children: _jsx(FaGavel, {}) }), _jsx(StatLabel, { children: "Active Auctions" }), _jsx(StatValue, { children: activeCount }), _jsx(StatSubtext, { children: "currently live" })] }), _jsxs(StatCard, { "$color": "#3b82f6", onClick: () => onNavigateToTab('active'), children: [_jsx(StatIcon, { "$color": "#3b82f6", children: _jsx(FaCalendarAlt, {}) }), _jsx(StatLabel, { children: "Upcoming" }), _jsx(StatValue, { children: scheduledCount }), _jsx(StatSubtext, { children: "scheduled to start" })] }), _jsxs(StatCard, { "$color": "#8b5cf6", onClick: () => onNavigateToTab('history'), children: [_jsx(StatIcon, { "$color": "#8b5cf6", children: _jsx(FaCheckCircle, {}) }), _jsx(StatLabel, { children: "Closed This Month" }), _jsx(StatValue, { children: closedThisMonth }), _jsx(StatSubtext, { children: "completed auctions" })] }), _jsxs(StatCard, { "$color": "#f59e0b", children: [_jsx(StatIcon, { "$color": "#f59e0b", children: _jsx(FaDollarSign, {}) }), _jsx(StatLabel, { children: "Avg Final Price" }), _jsx(StatValue, { children: analytics ? formatCurrency(analytics.avgFinalPrice) : '$0' }), _jsx(StatSubtext, { children: "across all auctions" })] })] }), analytics && (_jsxs(AnalyticsRow, { children: [_jsxs(AnalyticsCard, { children: [_jsx(AnalyticsLabel, { children: "Conversion Rate" }), _jsxs(AnalyticsValue, { children: [(analytics.conversionRate * 100).toFixed(1), "%"] })] }), _jsxs(AnalyticsCard, { children: [_jsx(AnalyticsLabel, { children: "Avg Bids per Auction" }), _jsx(AnalyticsValue, { children: analytics.avgBidCount.toFixed(1) })] }), _jsxs(AnalyticsCard, { children: [_jsx(AnalyticsLabel, { children: "Active Bidders" }), _jsx(AnalyticsValue, { children: analytics.activeBidderCount })] })] })), _jsxs(Section, { children: [_jsx(SectionHeader, { children: _jsx(SectionTitle, { children: "Quick Actions" }) }), _jsxs(QuickActionsGrid, { children: [_jsxs(QuickActionCard, { onClick: () => onNavigateToTab('create'), children: [_jsx(FaGavel, {}), _jsxs(QuickActionText, { children: [_jsx("h4", { children: "Create New Auction" }), _jsx("p", { children: "Start a new diamond auction" })] })] }), _jsxs(QuickActionCard, { onClick: () => onNavigateToTab('active'), children: [_jsx(FaClock, {}), _jsxs(QuickActionText, { children: [_jsx("h4", { children: "View Active Auctions" }), _jsx("p", { children: "Monitor live and scheduled" })] })] }), _jsxs(QuickActionCard, { onClick: () => onNavigateToTab('history'), children: [_jsx(FaUsers, {}), _jsxs(QuickActionText, { children: [_jsx("h4", { children: "Auction History" }), _jsx("p", { children: "Review past results" })] })] })] })] }), _jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "var(--accent-gold)", children: [_jsxs(SectionTitle, { children: [_jsx(FaGavel, {}), " Recent Auctions"] }), _jsx(SectionBadge, { "$color": "var(--accent-gold, #e6c068)", children: recentAuctions.length })] }), recentAuctions.length > 0 ? (_jsxs(_Fragment, { children: [_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Type" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Current Bid" }), _jsx(Th, { children: "Bids" }), _jsx(Th, { children: "Time" })] }) }), _jsx("tbody", { children: recentAuctions.slice(0, 8).map(auction => (_jsxs(Tr, { children: [_jsxs(Td, { children: [_jsxs("strong", { children: [auction.diamondSnapshot.caratWeight, "ct", ' ', auction.diamondSnapshot.shape, ' ', auction.diamondSnapshot.color, ' ', auction.diamondSnapshot.clarity] }), _jsx("div", { style: { fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.2rem' }, children: auction.diamondStockNumber || auction.diamondSnapshot.certificateNumber || 'No ref' })] }), _jsx(Td, { children: _jsx(TypeBadge, { "$type": auction.auctionType, children: auction.auctionType }) }), _jsx(Td, { children: _jsx(StatusBadge, { "$status": auction.status, children: auction.status.replace('_', ' ') }) }), _jsx(Td, { children: _jsx(PriceCell, { children: auction.currentBid > 0 ? formatCurrency(auction.currentBid) : '-' }) }), _jsx(Td, { children: auction.bidCount }), _jsx(Td, { children: auction.status === 'active' || auction.status === 'grace_period'
                                                            ? formatTimeRemaining(auction.scheduledEndTime)
                                                            : auction.status === 'scheduled'
                                                                ? `Starts ${new Date(auction.scheduledStartTime).toLocaleDateString()}`
                                                                : auction.actualEndTime
                                                                    ? new Date(auction.actualEndTime).toLocaleDateString()
                                                                    : '-' })] }, auction.id))) })] }) }), _jsx("div", { style: { textAlign: 'center', marginTop: '1rem' }, children: _jsxs(ViewAllButton, { onClick: () => onNavigateToTab('active'), children: ["View All Auctions ", _jsx(FaArrowRight, {})] }) })] })) : (_jsxs(EmptyState, { children: [_jsx(FaGavel, {}), _jsx("h3", { children: "No Auctions Yet" }), _jsx("p", { children: "Create your first diamond auction to see activity here." })] }))] })] }));
};
export default AuctionDashboardTab;
